// Replace previous questionId mapping with businessId (q1, q2 ...) mapping.
// We no longer derive tags from Mongo ObjectIds; we use backend-provided businessId.
// Extend these maps with all real businessIds from patient_survey.json / provider_survey.json.

import { TAGS } from '../util/constant'

/**
 * Patient survey businessId -> analytics tag
 */
export const PATIENT_BUSINESS_TAGS: Record<string, string> = {
  // Section identifiers
  c1: TAGS.OM_PA_PC_SECTION_COMM_STYLE,
  c2: TAGS.OM_PA_PC_SECTION_CARE_APPROACH,
  c3: TAGS.OM_PA_PC_SECTION_BACKGROUND,
  c4: TAGS.OM_PA_PC_SECTION_AMENITIES,
  q1: TAGS.OM_PA_PC_COMM_STYLE_LEAD_CONVO,
  q2: TAGS.OM_PA_PC_COMM_STYLE_DECISION_MAKER,
  q3: TAGS.OM_PA_PC_COMM_STYLE_SYMPTOMS_RESEARCH,
  q4: TAGS.OM_PA_PC_COMM_STYLE_LIST_QS,
  q5: TAGS.OM_PA_PC_COMM_STYLE_DIAGNOSIS_TREATMENT,
  q6: TAGS.OM_PA_PC_COMM_STYLE_DOCTOR_EXPLAINS_THINGS,
  q7: TAGS.OM_PA_PC_COMM_EDUCATIONAL_MATERIALS_DOCTOR,
  q8: TAGS.OM_PA_PC_COMM_STYLE_APPOINTMENT_PREF_DOCTOR,
  q9: TAGS.OM_PA_PC_COMM_STYLE_JOB_HEALTHY,

  // Care approach (sample – adjust if businessIds differ)
  q10: TAGS.OM_PA_PC_COMM_STYLE_CASUAL_OR_PROF,
  q11: TAGS.OM_PA_PC_COMM_STYLE_PRAYING_YN,
  q12: TAGS.OM_PA_PC_COMM_STYLE_ANOTHER_CLINICIAN,
  q13: TAGS.OM_PA_PC_CARE_APPROACH_CLINICAL_SERVICE,
  q14: TAGS.OM_PA_PC_CARE_APPROACH_NATURAL_INTEGRATIVE_SELECT,
  // Branching natural & integrative medicine treatments list
  'q14-1': TAGS.OM_PA_PC_CARE_APPROACH_NATURAL_INTEGRATIVE_TREATMENTS,
  q15: TAGS.OM_PA_PC_CARE_APPROACH_DIET_SUPPORT,
  // Branching diet selections (if needed, reuse same tag)
  'q15-1': TAGS.OM_PA_PC_CARE_APPROACH_DIET_SELECT,
  q16: TAGS.OM_PA_PC_CARE_APPROACH_MANAGE_PROBLEM,

  // Background
  q17: TAGS.OM_PA_PC_CARE_APPROACH_VACCINE,
  q18: TAGS.OM_PA_PC_CARE_APPROACH_KNOWLEDGE_EXP,
  q19: TAGS.OM_PA_PC_BACKGROUND_LANGUAGE_SELECT,
  'q19-1': TAGS.OM_PA_PC_BACKGROUND_LANGUAGE_SERVICE,
  q20: TAGS.OM_PA_PC_BACKGROUND_YOE,
  q21: TAGS.OM_PA_PC_AMENITIES_OFFICE,
  q22: TAGS.OM_PA_PC_AMENITIES_CONVENIENCE,
  q23: TAGS.OM_PA_PC_AMENITIES_DIGITAL,
  q24: TAGS.OM_PA_PC_AMENITIES_ADDITIONAL,
  q25: TAGS.OM_PA_PC_AMENITIES_INTEGRATED,
  q29: TAGS.OM_PA_PC_BACKGROUND_SEXUAL_ORIENTATION_SELECT,
  'q29-1': TAGS.OM_PA_PC_BACKGROUND_SEXUAL_ORIENTATION_YN,
  q30: TAGS.OM_PA_PC_BACKGROUND_RACE_ETHNICITY_SELECT,
  // Branching race/ethnicity preference (yes/no)
  'q30-1': TAGS.OM_PA_PC_BACKGROUND_RACE_ETHNICITY_YN,
  q31: TAGS.OM_PA_PC_BACKGROUND_MILITARY_YN,
  // Branching military doctor preference (yes/no)
  'q31-1': TAGS.OM_PA_PC_BACKGROUND_MILITARY_SELECT,
  q27: TAGS.OM_PA_PC_BACKGROUND_PRONOUN_YN,
  // Branching pronoun selection
  'q27-1': TAGS.OM_PA_PC_BACKGROUND_PRONOUN_SELECT,
  q28: TAGS.OM_PA_PC_BACKGROUND_GENDER_SELECT,
  'q28-1': TAGS.OM_PA_PC_BACKGROUND_YN,
  // Branching gender identity preference (yes/no)
  // Branching sexual orientation preference (yes/no) already mapped above
}

/**
 * Provider (clinician) survey businessId -> analytics tag
 */
export const PROVIDER_BUSINESS_TAGS: Record<string, string> = {
  // Section identifiers
  c1: TAGS.OM_CL_PC_SECTION_COMM_STYLE,
  c2: TAGS.OM_CL_PC_SECTION_CARE_APPROACH,
  c3: TAGS.OM_CL_PC_SECTION_BACKGROUND,
  // First clinician question uses businessId 'cq1' (fix mis-key 'q1')
  q1: TAGS.OM_CL_PC_COMM_STYLE_CONVERSATION_Lead,
  q2: TAGS.OM_CL_PC_COMM_STYLE_DECISION_MAKER,
  q3: TAGS.OM_CL_PC_COMM_STYLE_RESEARCH_SYMPTOMS,
  q4: TAGS.OM_CL_PC_COMM_STYLE_LIST_QS,
  q5: TAGS.OM_CL_PC_COMM_STYLE_DIAGNOSIS_TREATMENT,
  q6: TAGS.OM_CL_PC_COMM_STYLE_EXPLAINING_THINGS_PA,

  q7: TAGS.OM_CL_PC_COMM_STYLE_EDUCATIONAL_MATERIALS_PA,
  q8: TAGS.OM_CL_PC_COMM_STYLE_DURING_APPOINTMENT,
  q9: TAGS.OM_CL_PC_COMM_STYLE_JOB_HEALTHY,
  // cq9: TAGS.OM_CL_PC_COMM_STYLE_SYMPTOMS_RESEARCH,
  q10: TAGS.OM_CL_PC_COMM_STYLE_CASUAL_OR_PROF,
  q11: TAGS.OM_CL_PC_COMM_STYLE_PRAYING_YN,
  q12: TAGS.OM_CL_PC_COMM_STYLE_ANOTHER_CLINICIAN_YN,
  q13: TAGS.OM_CL_PC_CARE_APPROACH_CLINICAL_SERVICES,
  q14: TAGS.OM_CL_PC_CARE_APPROACH_NATURAL_INTEGRATIVE_SELECT,
  // Branching natural & integrative medicine treatments list
  'q14-1': TAGS.OM_CL_PC_CARE_APPROACH_NATURAL_INTEGRATIVE_SELECT,
  q15: TAGS.OM_CL_PC_CARE_NEW_DIET_SUPPORT,
  // Branching diet selections (if needed, reuse same tag)
  'q15-1': TAGS.OM_CL_PC_CARE_APPROACH_DIETS_SELECT,

  q16: TAGS.OM_CL_PC_CARE_APPROACH_PRESCRIPTION_SELECT,
  q17: TAGS.OM_CL_PC_VACCINATION_SCREENINGS,
  q18: TAGS.OM_CL_PC_CARE_TRAINING_EXP,
  q19: TAGS.OM_CL_PC_CARE_BIPOC_PA,
  q20: TAGS.OM_CL_PC_CARE_BODY_POSITIVE_APPROACH,
  q21: TAGS.OM_CL_PC_CARE_SUPPORTING_PA_CAREGIVERS,
  q22: TAGS.OM_CL_PC_CARE_COMMUNICATING_UNIQUE_NEEDS_PA,
  q23: TAGS.OM_CL_PC_CARE_END_OF_LIFE_CARE,
  q24: TAGS.OM_CL_PC_CARE_GENDER_AFFIRMING,
  q25: TAGS.OM_CL_PC_CARE_IMMIGRANTS_REFUGEES,
  q26: TAGS.OM_CL_PC_CARE_LGBTQ_PA,
  q27: TAGS.OM_CL_PC_CARE_COMPREH_MENS_CARE,
  q28: TAGS.OM_CL_PC_CARE_COMPREH_WOMENS_CARE,
  q29: TAGS.OM_CL_PC_CARE_NEURO_DIVERSITY,
  q30: TAGS.OM_CL_PC_CARE_PERIOD_POSITIVE_CARE,
  q31: TAGS.OM_CL_PC_CARE_SEX_POSITIVE_CARE,
  q32: TAGS.OM_CL_PC_CARE_SUBSTANCE_USE_ADDICTION,
  q33: TAGS.OM_CL_PC_CARE_TRAUMA_INFORMED,
  q34: TAGS.OM_CL_PC_CARE_VETERANS,
  q35: TAGS.OM_CL_PC_CARE_SPIRITUAL_SUPPORT,
  q36: TAGS.OM_CL_PC_CARE_APPROACH_KNOWLEDGE_EXP,
  q37: TAGS.OM_CL_PC_BACKGROUND_LANGUAGE_SELECT,
  q38: TAGS.OM_CL_PC_BACKGROUND_YOE,
  q39: TAGS.OM_CL_PC_BACKGROUND_PREFER_PA_ADDRESS,
  q40: TAGS.OM_CL_PC_BACKGROUND_PRONOUN_SELECT,
  q41: TAGS.OM_CL_PC_BACKGROUND_GENDER_SELECT,
  q42: TAGS.OM_CL_PC_BACKGROUND_SEXUAL_ORIENTATION,
  q43: TAGS.OM_CL_PC_BACKGROUND_RACE_ETHNICITY_SELECT,
  q44: TAGS.OM_CL_PC_BACKGROUND_MILITARY_YN,
  q45: TAGS.OM_CL_PC_CARE_APPROACH_OPINION,
  q46: TAGS.OM_CL_PC_BACKGROUND_RELIGIONS_SELECT,
  q47: TAGS.OM_CL_PC_BACKGROUND_POLITICAL_PARTY,
  q48: TAGS.OM_CL_PC_CARE_CANCER_MENTAL_PHYSICAL,
  q49: TAGS.OM_CL_PC_CARE_FAMILY_CENTERED_CARE,
}

export const resolveBusinessIdTag = (
  businessId: string | undefined,
  isProvider: boolean
): string | undefined => {
  if (!businessId) return undefined
  return isProvider
    ? PROVIDER_BUSINESS_TAGS[businessId]
    : PATIENT_BUSINESS_TAGS[businessId]
}
